import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { Store } from '@ngrx/store'
import * as lodash from 'lodash'
import {
  CalendarListComponent,
  CalendarListSelectionInfo,
  CalendarListViewPortInfo,
  CalendarEventTimeRange,
} from '@tokeet/ngx-calendar-list'

import * as fromRoot from '@tv3/store/state'
import {
  Destroyable,
  localTodayToUTC,
  RentalBookedDayTypes,
  itemsInTimeRange,
  toMoment,
  RentalRateItem,
} from '@tokeet-frontend/tv3-platform'
import { ExpectPermission } from '@tokeet-frontend/permission'
import {
  AiOffCalendarItemView,
  AiResponderSettings,
  isDateAIDisabled,
  toAiOffCalendarItemView,
} from '../../store/ai-settings.model'
import { ScheduleAiOffDialogService } from './schedule-ai-off-dialog/schedule-ai-off-dialog.service'
import { NgxPermissionsService } from 'ngx-permissions'
import { LoadAISettings } from '@tv3/containers/ai-responder/store/ai-settings.actions'

@Component({
  selector: 'app-ai-off-calendar',
  templateUrl: './ai-off-calendar.component.html',
  styleUrls: ['./ai-off-calendar.component.scss'],
})
export class AiOffCalendarComponent extends Destroyable implements OnInit {
  @Input() settings: AiResponderSettings

  years: number[]
  months: number[]
  events: AiOffCalendarItemView[]
  selectedMonth: number
  selectedYear: number

  blockedDates: Record<string, RentalBookedDayTypes> = {}

  today = localTodayToUTC().unix()

  @ViewChild('calendarList') calendarList: CalendarListComponent<AiOffCalendarItemView>

  constructor(
    private store: Store<fromRoot.State>,
    private permissions: NgxPermissionsService,
    private scheduleAiOffDialog: ScheduleAiOffDialogService
  ) {
    super()
    this.onViewportChange = lodash.debounce(this.onViewportChange, 250)
  }

  ngOnInit() {
    this.buildMonths()
    this.events = lodash.map(this.settings.disabledHours, toAiOffCalendarItemView)
  }

  getDateAvailability = (date: number) => {
    return isDateAIDisabled(date, this.settings.disabledHours)
  }

  getEventID = (event: AiOffCalendarItemView) => {
    return event.id
  }

  getEventTimeRange = (event: AiOffCalendarItemView): CalendarEventTimeRange => {
    return {
      start: event.start.unix(),
      end: event.end.unix(),
    }
  }

  isStartOfMonth(date: number) {
    return toMoment(date).date() === 1
  }

  isBeforeToday(date: number) {
    return toMoment(date).isBefore(toMoment(), 'date')
  }

  isInYear(month: number, year: number) {
    return toMoment(month).year() === year
  }

  canNavigate(active: number, list: number[], type: 'prev' | 'next') {
    if (type === 'prev') return active > lodash.first(list)
    if (type === 'next') return active < lodash.last(list)
    return false
  }

  getMonthDayData(rentalRatesByCategory: Record<string, Record<string, RentalRateItem>>, date: number) {
    const data = rentalRatesByCategory
    return lodash.mapValues(data, (t) => t[date])
  }

  refresh() {
    this.store.dispatch(LoadAISettings())
  }

  showEventDetails(event: AiOffCalendarItemView) {}

  onChangeMonth(type: 'next' | 'prev' = null, scroll = true) {
    const firstMonth = toMoment(lodash.first(this.months))
    const lastMonth = toMoment(lodash.last(this.months))

    let month = toMoment(this.selectedMonth)

    if (type === 'next') {
      month.add(1, 'month')
    } else if (type === 'prev') {
      month.subtract(1, 'month')
    }

    if (month.isBefore(firstMonth, 'month')) {
      month = firstMonth
    } else if (month.isAfter(lastMonth, 'month')) {
      month = lastMonth
    }

    this.selectedMonth = month.startOf('month').unix()
    this.selectedYear = month.year()

    if (scroll) this.calendarList.scrollToDate(this.selectedMonth)
  }

  onChangeYear(type: 'next' | 'prev' = null, scroll = true) {
    if (type === 'next') {
      if (this.selectedYear === lodash.last(this.years)) return
      this.selectedYear += 1
    } else if (type === 'prev') {
      if (this.selectedYear === lodash.first(this.years)) return
      this.selectedYear -= 1
    }

    this.selectedMonth = toMoment()
      .year(this.selectedYear)
      .month(toMoment(this.selectedMonth).month())
      .startOf('month')
      .unix()

    this.onChangeMonth(null, scroll)
  }

  onViewportChange(viewportInfo: CalendarListViewPortInfo) {
    const month = toMoment(viewportInfo.activeMonth)

    this.selectedMonth = month.startOf('month').unix()
    this.selectedYear = month.year()

    const start = month.clone().subtract(1, 'months').startOf('month').unix()
    const end = month.clone().add(1, 'months').endOf('month').unix()
    // this.fetchRates(start, end)
  }

  @ExpectPermission('readOnly')
  onSelectionChange(selectionInfo: CalendarListSelectionInfo) {
    if (selectionInfo.selectionType === 'click') {
      return
    }

    if (selectionInfo.selectionType === 'drag') {
      const dates = selectionInfo.selections

      if (!dates.length) return

      const start = lodash.minBy(dates)
      const end = lodash.maxBy(dates)

      // this.showSelectionDialog(start, end)
      this.calendarList.clearSelections()
      return
    }
  }

  onDayClick(day: number) {
    this.scheduleAiOffDialog.open({ date: day, timezone: this.settings.disabledTimezone })
  }

  gotoToday() {
    this.calendarList.scrollToDate(this.today)
  }

  private buildMonths() {
    const start = toMoment().startOf('month')
    const end = start.clone().add(24, 'month')

    this.months = itemsInTimeRange(start, end, 'month')

    this.years = itemsInTimeRange(start, end, 'year').map((time) => toMoment(time).year())
    this.selectedYear = lodash.first(this.years)

    this.onChangeYear(null, false)
  }
}
